import { type Routes } from '@medely/ui-kit';
import { MultiFactorError } from 'firebase/auth';
import {
  ArrayParam,
  BooleanParam,
  JsonParam,
  NumberParam,
  StringParam,
  withDefault,
} from 'use-query-params';

export type ForwardRoute = {
  [K in keyof ProRoutes]: {
    forwardTo: K;
    forwardParams: ProRoutes[K];
  };
}[keyof ProRoutes];

// Key = Route name
// Value = Params type
// Optional params should use | undefined, not ?
// Currently only primitive values are supported.
export type ProRoutes = {
  AdjustmentDetail: {
    id: number;
  };
  Application: never;
  AvailableAssignmentsLocal: never;
  EarningsPayoutDetails: {
    id: number;
  };
  EarningsPending: never;
  JobDetails: {
    id: number;
  };
  Shifts: never;
  Profile: never;
  ProPoolInvite: {
    locationId: number;
    facilityName: string;
    professionalQualificationId: number;
    plan: string;
  };
  ProPoolInviteAccepted: {
    facilityName: string;
  };
  LocationOnboarding: {
    location_id: number;
  };
  Today: never;
  PositionsAndSpecialties: never;
  PayoutTaxes: never;
  Credentials: never;
  RootCredentials: never;
  AccountDetails: { id: number | string };
  BackgroundCheckDisclosure: never;
  PayoutMethod: Partial<{ id: number | string }>;
  TaxInformation: Partial<{ id: number | string }>;
  MissingAndExpiringConditions: never;
  FindJobs: never;
  FindJobsPermanentPlacements: never;
  MfaSmsCodeEntryScreen:
    | { firebaseError?: MultiFactorError }
    | {
        forward?: ForwardRoute;
      };
  PermanentPlacementAvailableDetails: { id: number };
  SignIn: never;
  MfaIdentityVerificationScreen: {
    forward?: ForwardRoute;
  };
  Personal: never;
  CredentialDetails: {
    credential: never;
  };
  PasswordReset: never;
  IdentityVerification: never;
};

export const proRoutes: Routes<ProRoutes> = {
  AdjustmentDetail: {
    url: '/earnings/adjustments/:id',
  },
  Application: {
    url: '/application',
  },
  AvailableAssignmentsLocal: {
    url: '/available/assignments/local',
  },
  PermanentPlacementAvailableDetails: {
    url: '/available/permanent-placements/:id',
  },
  EarningsPayoutDetails: {
    url: '/earnings/payouts/:id',
  },
  EarningsPending: {
    url: '/earnings/pending',
  },
  JobDetails: {
    url: '/jobs/:id',
  },
  Shifts: {
    url: '/schedule',
  },
  Profile: {
    url: '/profile',
  },
  ProPoolInvite: {
    url: '/pro-pool/:locationId/invite',
  },
  ProPoolInviteAccepted: {
    url: '/pro-pool/invite-accepted',
  },
  LocationOnboarding: {
    url: 'onboarding/location/:location_id',
  },
  Today: {
    url: '/',
  },
  PositionsAndSpecialties: {
    url: '/profile/positions-and-specialties',
  },
  PayoutTaxes: {
    url: '/settings/financial',
  },
  AccountDetails: {
    url: '/settings/financial/:id/details',
  },
  BackgroundCheckDisclosure: {
    url: '/onboarding/background_check/disclosure',
  },
  PayoutMethod: {
    url: '/settings/financial/payout-method',
  },
  TaxInformation: {
    url: '/settings/financial/taxinfo',
  },
  Credentials: {
    url: '/credentials',
  },
  RootCredentials: {
    url: '/RootCredentials',
  },
  MissingAndExpiringConditions: {
    url: '/credentials/status',
  },
  FindJobsPermanentPlacements: {
    url: '/available/permanent-placements',
  },
  FindJobs: {
    url: '/available/jobs',
  },
  SignIn: {
    url: '/login',
  },
  MfaSmsCodeEntryScreen: {
    url: '/mfa-entry',
  },
  MfaIdentityVerificationScreen: {
    url: '/mfa-verification',
  },
  Personal: {
    url: '/profile/personal',
  },
  CredentialDetails: {
    url: '/credentials',
  },
  PasswordReset: {
    url: '/settings/password',
  },
  IdentityVerification: {
    url: '/settings/identity',
  },
};

export type RouteParamsWeb<T> = {
  [K in keyof T]:
    | Record<string, never>
    | {
        [key: string]:
          | typeof NumberParam
          | typeof StringParam
          | typeof BooleanParam
          | typeof ArrayParam
          | typeof JsonParam;
      };
};

export const proQueryParams: RouteParamsWeb<ProRoutes> = {
  AdjustmentDetail: {
    id: NumberParam,
  },
  Application: {},
  AvailableAssignmentsLocal: {},
  EarningsPayoutDetails: {
    id: NumberParam,
  },
  EarningsPending: {},
  JobDetails: {
    id: NumberParam,
  },
  Profile: {},
  ProPoolInvite: {
    locationId: NumberParam,
    facilityName: StringParam,
    professionalQualificationId: NumberParam,
    plan: StringParam,
  },
  ProPoolInviteAccepted: {
    facilityName: StringParam,
  },
  LocationOnboarding: {
    location_id: NumberParam,
  },
  Today: {},
  PayoutTaxes: {},
  TaxInformation: {},
  AccountDetails: {},
  BackgroundCheckDisclosure: {},
  Credentials: {},
  MissingAndExpiringConditions: {},
  PayoutMethod: {},
  PositionsAndSpecialties: {},
  RootCredentials: {},
  Shifts: {},
  FindJobs: {},
  FindJobsPermanentPlacements: {},
  PermanentPlacementAvailableDetails: {
    id: NumberParam,
  },
  SignIn: {},
  MfaSmsCodeEntryScreen: {
    forward: JsonParam,
    firebaseError: withDefault(JsonParam, null),
  },
  MfaIdentityVerificationScreen: {
    forward: JsonParam,
  },
  Personal: {},
  CredentialDetails: {
    credential: JsonParam,
  },
  PasswordReset: {},
  IdentityVerification: {},
};
